import { Component, forwardRef, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { SpecialHours } from '@generated/tcea/models';
import { requiredIfValidator, timeAfterValidator, timeBeforeValidator } from '@helpers/Validators';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { HoursUtilitiesService } from '@services/hours/1.0/hours-utilities.service';
import { DateService } from '@services/utilities/1.0/date.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgIf, NgFor } from '@angular/common';
import { ButtonComponent } from '../../button/1.0/button.component';
import { FormSubmittedSyncDirective } from '../../../directives/form-submitted-sync/1.0/form-submitted-sync.directive';
import { MatFormField, MatLabel, MatSuffix, MatError, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'txp-special-hours-template-form',
  templateUrl: './special-hours-template-form.component.html',
  styleUrls: ['./special-hours-template-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SpecialHoursTemplateFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SpecialHoursTemplateFormComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    NgIf,
    ButtonComponent,
    FormSubmittedSyncDirective,
    FormsModule,
    ReactiveFormsModule,
    NgFor,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatError,
    MatSlideToggle,
    MatHint,
    MatButton,
    TranslateModule,
  ],
})
export class SpecialHoursTemplateFormComponent
  implements ControlValueAccessor, Validator, OnDestroy, OnInit
{
  buttonStyle = ButtonStyle; // expose enum to template
  buttonSize = ButtonSize;
  form: UntypedFormGroup;
  onTouched: any;
  disabled: boolean;
  onChangeFn: any;
  private TIME_REGEX = HoursUtilitiesService.get24HourTimeRegex();
  private unsubscribe = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
    private dateService: DateService,
  ) {
    this.form = this.formBuilder.group({
      items: new UntypedFormArray([]),
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  writeValue(formValue: any): void {
    const formArray = this.form.get('items') as UntypedFormArray;

    // clear array
    formArray.clear();

    // add form controls for each item
    const controls: UntypedFormGroup[] = [];
    if (formValue) {
      for (const value of formValue) {
        controls.push(this.createListItem(value));
      }
    }
    // set new FormArray all at once to avoid unnecessary change events
    this.form.setControl('items', new UntypedFormArray(controls));
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
    this.form.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      const hours = HoursUtilitiesService.convertSpecialHoursUIToSpecialHours(data.items);
      fn(hours);
    });
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    isDisabled ? this.form.disable() : this.form.enable();
  }

  validate(c: AbstractControl): ValidationErrors | null {
    return this.form.valid
      ? undefined
      : {
          invalidForm: {
            valid: false,
            message: this.translateService.instant('form.error.fieldsAreInvalid'),
          },
        };
  }

  ngOnInit(): void {
    this.form.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      const items = this.form.get('items') as UntypedFormArray;

      items.controls.forEach((formGroup: UntypedFormGroup) => {
        const isOpen = formGroup.get('isOpen').value;
        const hoursGroup = (formGroup.get('operatingHours') as UntypedFormArray).at(
          0,
        ) as UntypedFormGroup;

        const action = isOpen ? 'enable' : 'disable';
        hoursGroup.get('startTime')[action]({ emitEvent: false });
        hoursGroup.get('endTime')[action]({ emitEvent: false });

        if (!isOpen) {
          hoursGroup.patchValue({ startTime: '', endTime: '' }, { emitEvent: false });
        }
      });
    });
  }

  /**
   * Creates a form instance of SpecialHoursItem
   */
  createListItem(value: SpecialHours): UntypedFormGroup {
    let ruleValue;
    let startTime = '';
    let endTime = '';
    let isOpen = true;
    if (value) {
      if (value.rule) {
        // convert YYYY-MM-DD to date object for datepicker
        ruleValue = this.dateService.toDate(value.rule.value);
      }

      if (value.operatingHours) {
        const operatingHours = value.operatingHours;
        if (operatingHours.length > 0) {
          startTime = operatingHours[0].startTime;
          endTime = operatingHours[0].endTime;
        } else {
          isOpen = false;
        }
      }
    }
    const isOpenCtrl = new UntypedFormControl(isOpen);
    const formGroup = this.formBuilder.group({
      rule: new UntypedFormGroup({
        type: new UntypedFormControl('fixedDate'),
        value: new UntypedFormControl(ruleValue, Validators.required),
      }),
      isOpen: isOpenCtrl,
      operatingHours: new UntypedFormArray([
        new UntypedFormGroup({
          startTime: new UntypedFormControl({ disabled: !isOpen, value: startTime }, [
            requiredIfValidator(() => isOpenCtrl.value),
            Validators.pattern(this.TIME_REGEX),
          ]),
          endTime: new UntypedFormControl({ disabled: !isOpen, value: endTime }, [
            requiredIfValidator(() => isOpenCtrl.value),
            Validators.pattern(this.TIME_REGEX),
          ]),
        }),
      ]),
    });
    formGroup
      .get('isOpen')
      .valueChanges.pipe(takeUntil(this.unsubscribe))
      .subscribe((open: boolean) => {
        const operatingHours: UntypedFormArray = formGroup.get(
          'operatingHours',
        ) as UntypedFormArray;
        const hours: UntypedFormGroup = operatingHours.at(0) as UntypedFormGroup;
        if (open) {
          hours.enable();
        } else {
          hours.disable();
          hours.setValue({ startTime: '', endTime: '' });
        }
      });

    const startTimeField = formGroup.controls.operatingHours.get('0').get('startTime');
    const endTimeField = formGroup.controls.operatingHours.get('0').get('endTime');

    // Need to make sure to include current validator function too
    endTimeField.setValidators([endTimeField.validator, timeAfterValidator(startTimeField)]);
    startTimeField.setValidators([startTimeField.validator, timeBeforeValidator(endTimeField)]);

    formGroup.valueChanges.subscribe(() => {
      startTimeField.updateValueAndValidity({ emitEvent: false });
      endTimeField.updateValueAndValidity({ emitEvent: false });
    });
    return formGroup;
  }

  /**
   * Adds a new SpecialHoursItem to list
   */
  addListItem(value: SpecialHours) {
    const items = this.form.get('items') as UntypedFormArray;
    items.insert(0, this.createListItem(value));
  }

  /**
   * Removes clicked row item from list
   */
  removeListItem(index) {
    const items = this.form.get('items') as UntypedFormArray;
    items.removeAt(index);
  }
}
